
import EliminatedTeamItem from "@/components/Standings/EliminatedTeamItem.vue";
import { getNames } from "../use/owner-mapping";
import { GuillotineTeam } from "../shared/teams/team";
import Matchup from "@/shared/matchups/matchup";
import orderBy from "lodash/orderBy";
import Player from "@/shared/players/player";
import { ref } from "vue";
import Season from "@/shared/seasons/season";
import StandingsRow from "../components/Standings/StandingsRow.vue";
import { teamApi } from "../shared/teams/team-api";
import { TeamScoresDictionary } from "../shared/teams/team";

function useGuillotineCalculator() {
  const currentMatchups = ref<Matchup[]>([]);
  const currentWeek = ref<number>(0);
  const cutTeams = ref<GuillotineTeam[]>([]);
  const loading = ref<boolean>(false);
  const teams = ref<GuillotineTeam[]>([]);
  const weeks = ref<number[]>([]);
  interface RoundSettings {
    weeks: Array<number>;
    playersToCut: number;
  }
  interface RoundSettingsMap {
    [key: number]: RoundSettings;
  }
  const rounds: RoundSettingsMap = {
    1: { weeks: [1, 2, 3, 4], playersToCut: 1 },
    2: { weeks: [1, 2, 3, 4], playersToCut: 1 },
    3: { weeks: [1, 2, 3, 4], playersToCut: 1 },
    4: { weeks: [1, 2, 3, 4], playersToCut: 1 },
    5: { weeks: [5, 6], playersToCut: 2 },
    6: { weeks: [5, 6], playersToCut: 2 },
    7: { weeks: [7, 8], playersToCut: 2 },
    8: { weeks: [7, 8], playersToCut: 2 },
    9: { weeks: [9, 10], playersToCut: 2 },
    10: { weeks: [9, 10], playersToCut: 2 },
    11: { weeks: [11, 12], playersToCut: 2 },
    12: { weeks: [11, 12], playersToCut: 2 },
    13: { weeks: [13, 14], playersToCut: 2 },
    14: { weeks: [13, 14], playersToCut: 2 },
    15: { weeks: [15, 16], playersToCut: 2 },
    16: { weeks: [15, 16], playersToCut: 2 },
    17: { weeks: [17], playersToCut: 1 },
  };

  function getMatchupScores(matchups: Matchup[]): TeamScoresDictionary {
    const teamScores: TeamScoresDictionary = {} as TeamScoresDictionary;

    for (let i = 0; i < matchups.length; i++) {
      const matchup: Matchup = matchups[i];

      if (matchup?.away?.teamId && teamScores[matchup.away.teamId]) {
        teamScores[matchup.away.teamId] +=
          (matchup.away.totalPoints || 0) + (matchup.away.totalPointsLive || 0);
      } else if (matchup?.away?.teamId) {
        teamScores[matchup.away.teamId] =
          (matchup.away.totalPoints || 0) + (matchup.away.totalPointsLive || 0);
      }

      if (matchup?.home?.teamId && teamScores[matchup.home.teamId]) {
        teamScores[matchup.home.teamId] +=
          (matchup.home.totalPoints || 0) + (matchup.home.totalPointsLive || 0);
      } else if (matchup?.home?.teamId) {
        teamScores[matchup.home.teamId] =
          (matchup.home.totalPoints || 0) + (matchup.home.totalPointsLive || 0);
      }
    }

    return teamScores;
  }

  function isPlayerLive(liveNflTeams: string[], player: Player): boolean {
    return liveNflTeams.includes(
      player.playerPoolEntry.player.proTeamId.toString()
    );
  }

  function getPlayersRemaining(liveNflTeams: string[]) {
    const lastWeekOfRound = Math.max(...weeks.value);
    for (let i = 0; i < teams.value.length; i++) {
      let remaining = 0;
      let inPlay = 0;
      const team: GuillotineTeam = teams.value[i];
      for (let j = 0; j < team.roster.entries.length; j++) {
        const player = team.roster.entries[j];
        if (player.lineupSlotId < 20 && !player.playerPoolEntry.lineupLocked) {
          remaining++;
        } else if (
          player.lineupSlotId < 20 &&
          isPlayerLive(liveNflTeams, player)
        ) {
          inPlay++;
        }
      }
      teams.value[i].playersRemaining = remaining;
      teams.value[i].inPlay = inPlay;

      if (lastWeekOfRound > currentWeek.value) {
        teams.value[i].playersRemaining += 10 * (lastWeekOfRound - currentWeek.value);
      }
    }
  }

  function getPointsFromSafety(numToCut: number) {
    const highestScoreBelowTheCutLine =
      teams.value.length > numToCut
        ? teams.value[teams.value.length - numToCut].roundScore
        : teams.value[1].roundScore;

    const lowestScoreAboveTheCutLine = 
      teams.value.length > numToCut - 1
        ? teams.value[teams.value.length - numToCut - 1].roundScore
        : teams.value[1].roundScore;

    for (let i = 0; i < teams.value.length; i++) {
      const team = teams.value[i];

      if (i <= teams.value.length - 3 && teams.value.length > 2) {
        team.pointsFromSafety = team.roundScore - highestScoreBelowTheCutLine;
      } else if (i > teams.value.length - 3) {
        team.pointsFromSafety = team.roundScore - lowestScoreAboveTheCutLine;
      }
    }
  }

  function getStatuses() {
    const finishedTeams = orderBy(
      teams.value.filter((team: GuillotineTeam) => {
        return team.playersRemaining === 0 && team.inPlay === 0;
      }),
      ["roundScore"],
      ["desc"]
    );

    for (let i = 0; i < teams.value.length; i++) {
      const team = teams.value[i];
      if (
        team.playersRemaining + team.inPlay === 0 &&
        team.pointsFromSafety < 0
      )
        team.status = 0;
      else if (
        finishedTeams.length > 1 &&
        team.roundScore > finishedTeams[finishedTeams.length - 2].roundScore
      )
        team.status = 3;
      else if (team.pointsFromSafety >= 0) team.status = 2;
      else team.status = 1;
    }
  }

  function getMostRecentCuts(
    schedule: Matchup[],
    teams: GuillotineTeam[],
    previousScoringPeriodWeeks: number[]
  ): GuillotineTeam[] {
    if (currentWeek.value <= 2) return [];

    const previousMatchups = schedule.filter((matchup: Matchup) => {
      return previousScoringPeriodWeeks.includes(matchup.matchupPeriodId);
    });

    const scores: TeamScoresDictionary = getMatchupScores(previousMatchups);

    for (let i = 0; i < teams.length; i++) {
      const team: GuillotineTeam = teams[i];
      const roundScore: number = scores[team.id] || 0;
      team.roundScore = roundScore;
    }

    teams = teams.filter((team) => team.roundScore > 0);
    return orderBy(teams, ["roundScore"], ["desc"]);
  }

  async function getliveNflTeams(): Promise<string[]> {
    const nflGames = await teamApi.getNflGames();
    const liveTeams: string[] = [];
    nflGames.events.filter((game: any) => {
      if (game.status === "in") {
        liveTeams.push(game.competitors[0].id);
        liveTeams.push(game.competitors[1].id);
      }
    });

    return liveTeams;
  }

  async function getGuillotineStandings() {
    loading.value = true;

    const data: Season = await teamApi.getMatchups(1269171);
    let roundHasStarted = false;

    currentWeek.value = data.scoringPeriodId;
    weeks.value.push(...rounds[currentWeek.value].weeks);

    teams.value = (data.teams as GuillotineTeam[]).filter(
      (team: GuillotineTeam) => {
        return team.roster.entries.length > 0;
      }
    );

    currentMatchups.value = data.schedule.filter((matchup: Matchup) => {
      return weeks.value.includes(matchup.matchupPeriodId);
    });

    const scores: TeamScoresDictionary = getMatchupScores(
      currentMatchups.value
    );
    for (let i = 0; i < teams.value.length; i++) {
      const team: GuillotineTeam = teams.value[i];
      const roundScore: number = scores[team.id] || 0;
      team.roundScore = roundScore;

      // todo a better way to handle this would be to see if any games are either in progress or final.
      if (roundScore > 0) roundHasStarted = true;
    }

    if (!roundHasStarted) {
      cutTeams.value = getMostRecentCuts(
        data.schedule,
        data.teams as GuillotineTeam[],
        [...rounds[currentWeek.value - 1].weeks]
      );
    } else {
      teams.value = orderBy(teams.value, ["roundScore"], ["desc"]);
    }

    const liveNflTeams = await getliveNflTeams();
    getPlayersRemaining(liveNflTeams);
    getPointsFromSafety(rounds[currentWeek.value].playersToCut);
    getStatuses();
    teams.value = getNames(
      data.members,
      teams.value as GuillotineTeam[]
    ) as GuillotineTeam[];
    loading.value = false;
  }

  getGuillotineStandings();

  return {
    currentMatchups,
    currentWeek,
    cutTeams,
    getGuillotineStandings,
    loading,
    teams,
    weeks,
  };
}

export default {
  components: { EliminatedTeamItem, StandingsRow },
  setup() {
    return useGuillotineCalculator();
  },
};
